import { useState, useEffect, useCallback } from 'react';
import { getRegistrosByEmail, getInvestigadores } from '../utils/storage';
import { InvestigadorRecord, Publicacion } from '../types';

interface Props {
  email: string;
  /** Se incrementa cada vez que el usuario envía un nuevo registro, para refrescar */
  refreshKey?: number;
  /** Si true, carga TODOS los registros del censo (solo para admin) */
  isAdmin?: boolean;
}

function getBadgeBase(baseDatos: string, baseDatosOtra: string): string {
  if (!baseDatos) return '';
  if (baseDatos === 'Otras (especificar)') return baseDatosOtra || 'Otra';
  return baseDatos;
}

function PubCard({ pub, index }: { pub: Publicacion; index: number }) {
  const [showDoc, setShowDoc] = useState(false);
  const badge = getBadgeBase(pub.baseDatos, pub.baseDatosOtra);

  const tieneLink = !!pub.link.trim();
  const tieneArchivo = !!pub.archivoNombre.trim();
  const archivoUrl = pub.archivoUrl ?? '';
  const tieneEnlace = tieneLink || tieneArchivo;

  const isPdf = pub.archivoNombre.toLowerCase().endsWith('.pdf');

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      {/* Header de la publicación */}
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="w-7 h-7 flex items-center justify-center bg-[#0D3157] rounded-full flex-shrink-0 mt-0.5">
          <span className="text-white text-xs font-bold">{index + 1}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-700 leading-snug">{pub.titulo}</p>
          {badge && (
            <span className="inline-flex items-center gap-1 mt-1.5 bg-[#0D3157]/8 text-[#0D3157] text-xs font-medium px-2 py-0.5 rounded-full">
              <i className="ri-database-2-line text-xs" />
              {badge}
            </span>
          )}
        </div>
      </div>

      {/* Enlace / archivo */}
      {tieneEnlace && (
        <div className="border-t border-slate-100 px-4 py-2.5 bg-slate-50/60">
          {tieneLink && (
            <a
              href={pub.link}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center gap-2 text-xs text-[#0D3157] font-medium hover:underline cursor-pointer min-w-0 mb-2 last:mb-0"
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-external-link-line text-sm" />
              </div>
              <span className="truncate">{pub.link}</span>
            </a>
          )}

          {tieneArchivo && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className={`text-sm ${isPdf ? 'ri-file-pdf-line text-red-500' : 'ri-file-word-line text-sky-600'}`} />
                </div>
                <span className="text-xs text-slate-600 font-medium truncate">{pub.archivoNombre}</span>
              </div>

              {/* Botones de acción — siempre visibles si hay URL */}
              {archivoUrl ? (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {isPdf && (
                    <button
                      type="button"
                      onClick={() => setShowDoc((v) => !v)}
                      className="flex items-center gap-1 text-xs text-[#0D3157] hover:text-[#0a2444] font-semibold bg-[#0D3157]/8 hover:bg-[#0D3157]/15 px-2.5 py-1 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className={`text-xs ${showDoc ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
                      {showDoc ? 'Ocultar' : 'Ver PDF'}
                    </button>
                  )}
                  <a
                    href={archivoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={pub.archivoNombre}
                    className="flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800 font-semibold bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-1 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-download-2-line text-xs" />
                    Descargar
                  </a>
                </div>
              ) : (
                <span className="text-xs text-amber-500 flex items-center gap-1 flex-shrink-0">
                  <i className="ri-time-line text-xs" />
                  Procesando…
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Preview embebido del archivo PDF */}
      {showDoc && archivoUrl && isPdf && (
        <div className="border-t border-slate-200">
          <div className="flex items-center justify-between px-4 py-2 bg-emerald-50 border-b border-emerald-100">
            <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1.5">
              <i className="ri-file-text-line" />
              {pub.archivoNombre}
            </span>
            <a
              href={archivoUrl}
              target="_blank"
              rel="noopener noreferrer"
              download={pub.archivoNombre}
              className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line" />
              Abrir en nueva pestaña
            </a>
          </div>
          <div className="p-3 bg-slate-50">
            <iframe
              src={archivoUrl}
              title={pub.archivoNombre}
              className="w-full h-72 rounded border border-slate-200"
            />
          </div>
        </div>
      )}

      {!tieneEnlace && (
        <div className="border-t border-slate-100 px-4 py-2 bg-slate-50/50">
          <span className="text-xs text-slate-400 italic flex items-center gap-1">
            <i className="ri-link-unlink-m text-xs" />
            Sin enlace ni archivo adjunto
          </span>
        </div>
      )}
    </div>
  );
}

function RegistroCard({
  record,
  defaultOpen,
  showOwner,
}: {
  record: InvestigadorRecord;
  defaultOpen?: boolean;
  showOwner?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const archivosCount = record.publicaciones.filter((p) => p.archivoNombre && p.archivoUrl).length;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      {/* Header colapsable */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors cursor-pointer text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 flex items-center justify-center bg-[#0D3157]/10 rounded-lg flex-shrink-0">
            <i className="ri-file-text-line text-[#0D3157] text-base" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#0D3157] truncate">
              {record.nombre} {record.apellido}
            </p>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              <span className="text-xs text-slate-400">{record.fechaRegistro}</span>
              {showOwner && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="text-xs text-slate-500 truncate max-w-[160px]">{record.email}</span>
                </>
              )}
              <span className="text-slate-300">·</span>
              <span className="text-xs text-slate-500 truncate max-w-[120px]">{record.unidad}</span>
              <span className="text-slate-300">·</span>
              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                record.tipoUsuario === 'docente'
                  ? 'bg-[#0D3157]/10 text-[#0D3157]'
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {record.tipoUsuario === 'docente' ? 'Docente' : 'Estudiante'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          <span className="hidden sm:flex items-center gap-1 text-xs font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
            <i className="ri-book-2-line text-xs" />
            {record.publicaciones.length}{' '}
            {record.publicaciones.length === 1 ? 'pub.' : 'pubs.'}
          </span>
          {archivosCount > 0 && (
            <span className="hidden sm:flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
              <i className="ri-attachment-2 text-xs" />
              {archivosCount}
            </span>
          )}
          <div className="w-6 h-6 flex items-center justify-center">
            <i className={`text-slate-400 text-base transition-transform duration-200 ${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
          </div>
        </div>
      </button>

      {/* Contenido expandido */}
      {open && (
        <div className="border-t border-slate-200">
          {/* Datos del registro */}
          <div className="px-5 py-4 bg-slate-50/50 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {record.profesion && (
              <div className="flex items-center gap-2">
                <i className="ri-graduation-cap-line text-slate-400 text-xs flex-shrink-0" />
                <span className="text-xs text-slate-500 truncate">{record.profesion}</span>
              </div>
            )}
            {record.cargo && (
              <div className="flex items-center gap-2">
                <i className="ri-briefcase-line text-slate-400 text-xs flex-shrink-0" />
                <span className="text-xs text-slate-500 truncate">
                  {record.cargo}
                  {record.materiaDocente ? ` — ${record.materiaDocente}` : ''}
                  {record.cargoAdminDetalle ? ` — ${record.cargoAdminDetalle}` : ''}
                </span>
              </div>
            )}
            {record.telefono && (
              <div className="flex items-center gap-2">
                <i className="ri-phone-line text-slate-400 text-xs flex-shrink-0" />
                <span className="text-xs text-slate-500">{record.telefono}</span>
              </div>
            )}
            {record.orcid && (
              <div className="flex items-center gap-2 sm:col-span-2">
                <i className="ri-id-card-line text-slate-400 text-xs flex-shrink-0" />
                <a
                  href={record.orcid}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-xs text-[#0D3157] hover:underline truncate cursor-pointer"
                >
                  {record.orcid}
                </a>
              </div>
            )}
          </div>

          {/* Publicaciones */}
          <div className="px-5 py-4">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
              Publicaciones registradas ({record.publicaciones.length})
            </p>
            {record.publicaciones.length > 0 ? (
              <div className="space-y-2">
                {record.publicaciones.map((pub, idx) => (
                  <PubCard key={pub.id} pub={pub} index={idx} />
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">Este registro no tiene publicaciones asociadas.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MisPublicaciones({ email, refreshKey = 0, isAdmin = false }: Props) {
  const [records, setRecords] = useState<InvestigadorRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    const data = isAdmin ? await getInvestigadores() : await getRegistrosByEmail(email);
    setRecords(data);
    setLoading(false);
  }, [email, isAdmin]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords, refreshKey]);

  const totalPublicaciones = records.reduce((acc, r) => acc + r.publicaciones.length, 0);
  const totalArchivos = records.reduce(
    (acc, r) => acc + r.publicaciones.filter((p) => p.archivoNombre && p.archivoUrl).length,
    0
  );

  const filtered = search.trim()
    ? records.filter((r) => {
        const q = search.toLowerCase();
        return (
          r.nombre.toLowerCase().includes(q) ||
          r.apellido.toLowerCase().includes(q) ||
          r.email.toLowerCase().includes(q) ||
          r.unidad.toLowerCase().includes(q)
        );
      })
    : records;

  return (
    <section className="max-w-5xl mx-auto px-6 mt-8">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${isAdmin ? 'bg-emerald-600' : 'bg-[#0D3157]'}`}>
              <i className={`text-white text-sm ${isAdmin ? 'ri-team-line' : 'ri-history-line'}`} />
            </div>
            <div>
              <h2 className="text-[#0D3157] font-bold text-base">
                {isAdmin ? 'Todos los investigadores y publicaciones' : 'Mis registros y publicaciones'}
              </h2>
              <p className="text-slate-400 text-xs">
                {isAdmin
                  ? 'Vista completa del censo — todos los registros enviados'
                  : 'Historial de todos los aportes que ha enviado al censo'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!loading && records.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="bg-[#0D3157]/8 text-[#0D3157] text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                  <i className="ri-file-list-3-line text-xs" />
                  {records.length} {records.length === 1 ? 'envío' : 'envíos'}
                </span>
                <span className="bg-emerald-50 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                  <i className="ri-book-open-line text-xs" />
                  {totalPublicaciones} {totalPublicaciones === 1 ? 'publicación' : 'publicaciones'}
                </span>
                {totalArchivos > 0 && (
                  <span className="bg-orange-50 text-orange-700 text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 border border-orange-200">
                    <i className="ri-attachment-2 text-xs" />
                    {totalArchivos} archivo{totalArchivos !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={fetchRecords}
              disabled={loading}
              className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-[#0D3157] hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
              title="Actualizar"
            >
              <i className={`ri-refresh-line text-base ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Buscador — solo para admin o cuando hay muchos registros */}
        {(isAdmin || records.length > 3) && !loading && records.length > 0 && (
          <div className="px-6 py-3 border-b border-slate-100">
            <div className="relative max-w-sm">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-slate-400 text-sm" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={isAdmin ? 'Buscar por nombre, correo, profesión…' : 'Buscar publicación…'}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-xs text-slate-600 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0D3157]/20 focus:border-[#0D3157] bg-white"
              />
            </div>
          </div>
        )}

        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center py-10 gap-3 text-slate-400">
              <i className="ri-loader-4-line animate-spin text-[#0D3157] text-3xl" />
              <p className="text-sm text-slate-500">
                {isAdmin ? 'Cargando todos los registros del censo…' : 'Cargando sus registros…'}
              </p>
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-3">
                <i className="ri-inbox-line text-3xl text-slate-300" />
              </div>
              <p className="text-sm font-semibold text-slate-500 mb-1">
                {isAdmin ? 'No hay registros en el censo todavía' : 'Aún no tiene registros enviados'}
              </p>
              <p className="text-xs text-slate-400 max-w-xs mx-auto leading-relaxed">
                {isAdmin
                  ? 'Los registros aparecerán aquí cuando los investigadores completen el formulario.'
                  : 'Complete el formulario de arriba y envíe su primer registro. Aparecerá aquí automáticamente.'}
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-2">
                <i className="ri-search-line text-xl text-slate-300" />
              </div>
              <p className="text-sm font-medium text-slate-500">Sin resultados para &quot;{search}&quot;</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((record, idx) => (
                <RegistroCard
                  key={record.id}
                  record={record}
                  defaultOpen={!isAdmin && idx === 0}
                  showOwner={isAdmin}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
